/**
 * D-2 订单 —— `/api/orders`（契约 §7 D-2，2026-09-03 已建成）。
 *
 * ★ 契约硬约束：**只读 + 可取消，永不建单**。本层只有 list / detail / cancel。
 * ⚠ 取消只管「出票前撤单」，不是退票 —— 两者别在界面上做成同一个按钮（见 orders 契约）。
 * ⚠ 都要登录（网关「匿名禁止 `/api/orders/**`」），默认 `auth: 'required'`。
 */
import {
  cancelResultSchema,
  orderDetailSchema,
  orderSummarySchema,
  pageResultSchema,
  type CancelOrderRequest,
  type CancelResult,
  type OrderDetail,
  type OrderSummary,
  type PageResult,
} from '@cione/contracts'
import type { HttpClient } from './http'

const orderPageSchema = pageResultSchema(orderSummarySchema)

export interface OrdersApi {
  /**
   * 我的订单列表。⚠ `status` 传我方状态名（`ORDER_PAID` 等），认不出后端会 400 ——
   *   所以只把已知状态给筛选器，别让用户填自由文本。
   */
  list(params?: {
    status?: string
    page?: number
    size?: number
  }): Promise<PageResult<OrderSummary>>
  /** 详情。⚠ 404 不区分「不存在」与「不是你的」—— 文案写「找不到这个订单」。 */
  detail(orderNo: string): Promise<OrderDetail>
  /**
   * 取消（出票前撤单）。⚠⚠ 返回的 `status` 是**真实新状态**：已付款取消后是
   *   `ORDER_REFUNDING`，不是 `ORDER_CANCELLED`。按它显示，别写死「取消成功 = 已取消」。
   */
  cancel(orderNo: string, body?: CancelOrderRequest): Promise<CancelResult>
}

export function createOrdersApi(http: HttpClient): OrdersApi {
  return {
    list(params) {
      return http.request({
        method: 'GET',
        path: '/api/orders',
        query: { status: params?.status, page: params?.page, size: params?.size },
        schema: orderPageSchema,
      })
    },

    detail(orderNo) {
      return http.request({
        method: 'GET',
        path: `/api/orders/${encodeURIComponent(orderNo)}`,
        schema: orderDetailSchema,
      })
    },

    cancel(orderNo, body) {
      return http.request({
        method: 'POST',
        path: `/api/orders/${encodeURIComponent(orderNo)}/cancel`,
        body: body ?? {},
        schema: cancelResultSchema,
      })
    },
  }
}
